<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import 'driver.js/dist/driver.css';
import { useOnboardingStore } from '@/stores/onboarding';
import { useLayout } from '@/layout/composables/layout';
import {
    isExpectedOnboardingPage,
    readOnboardingPath,
} from '@/composables/onboardingPath.mjs';

const route = useRoute();
const toast = useToast();
const store = useOnboardingStore();
const { layoutState } = useLayout();
const tour = ref(null);
let startTimer = null;

const accountingPageSteps = {
    '/budget': () => [
        {
            element: '[data-onboarding="nav-budget"]',
            popover: {
                title: 'Budget lives here',
                description: 'Use the Budget page to manage transaction categories and annual budget amounts.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="budget-actions"]',
            popover: {
                title: 'Set up transaction categories',
                description: 'Review the starting categories, edit their annual amounts, and add or remove categories to match your association’s budget.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="nav-accounts"]',
            popover: {
                title: 'Next: link your bank',
                description: 'When your categories are ready, choose Bank Accounts from the sidebar.',
                side: 'right',
            },
        },
    ],
    '/accounts': () => [
        ...(!store.accountingProgress.categories ? [{
            element: '[data-onboarding="nav-budget"]',
            popover: {
                title: 'Set up categories first',
                description: 'Use Budget to set your transaction categories before importing bank activity.',
                side: 'right',
            },
        }] : []),
        {
            element: '[data-onboarding="nav-accounts"]',
            popover: {
                title: 'Bank Accounts live here',
                description: 'Use the Bank Accounts page to connect, reconnect, or check the status of an account.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="link-bank"]',
            popover: {
                title: 'Connect your operating account',
                description: 'SmartHOA imports transactions securely so it can organize spending by vendor and category.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="nav-reports"]',
            popover: {
                title: 'Next: review the report',
                description: 'After transactions arrive, choose Reports from the sidebar to review where the association’s money is going.',
                side: 'right',
            },
        },
    ],
    '/transactions': () => [
        {
            element: '[data-onboarding="nav-transactions"]',
            popover: {
                title: 'Transactions live here',
                description: 'Use the Transactions page to view transactions and update the vendor or category assigned to a transaction.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="transactions-table"]',
            popover: {
                title: 'Correct imported activity',
                description: 'You can edit the vendor or category for any transaction directly in the table. Your reports update from these assignments.',
                side: 'top',
            },
        },
        {
            element: '[data-onboarding="nav-reports"]',
            popover: {
                title: 'Return to Reports',
                description: 'Use Reports to view your spending breakdown.',
                side: 'right',
            },
        },
    ],
    '/reports': (reportReady) => reportReady ? [
        {
            element: '[data-onboarding="nav-reports"]',
            popover: {
                title: 'Reports live here',
                description: 'Use the Reports page to review spending by category and vendor.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="reports-content"]',
            popover: {
                title: 'Review your spending',
                description: 'Check that the category and vendor assignments match what you expect. If an expense is mis-categorized you can use the Transactions page to correct it.',
                side: 'top',
            },
        },
        {
            element: '[data-onboarding="nav-transactions"]',
            popover: {
                title: 'Need to fix something?',
                description: 'Open Transactions from the sidebar to correct a vendor or category, then return here.',
                side: 'right',
            },
        }
    ] : [
        {
            element: '[data-onboarding="nav-reports"]',
            popover: {
                title: 'Reports live here',
                description: 'Your spending breakdown will appear here after bank transactions finish importing.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="reports-content"]',
            popover: {
                title: 'Transactions are still syncing',
                description: 'There is nothing to review yet. Imported activity can take a little while to appear.',
                side: 'top',
            },
        },
        {
            element: '[data-onboarding="nav-transactions"]',
            popover: {
                title: 'Check imported transactions',
                description: 'Use Transactions to see when activity arrives, then return to Reports to finish setup.',
                side: 'right',
            },
        },
    ],
};

const duesPageSteps = {
    '/members': () => store.duesProgress.members ? [
        {
            element: '[data-onboarding="nav-members"]',
            popover: {
                title: 'Members live here',
                description: 'Use the Members page to add association members, update contact details, and choose who receives invoices for each unit.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="members-table"]',
            popover: {
                title: 'Your first member is ready',
                description: 'The member who receives invoices should show Yes in the Receives Invoices column.',
                side: 'top',
            },
        },
        {
            element: '[data-onboarding="import-members"]',
            popover: {
                title: 'Import additional members',
                description: 'Use a CSV or Excel file when you need to add several members at once. You can preview and correct the rows before importing.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="nav-dues"]',
            popover: {
                title: 'Next: assign dues',
                description: 'Choose Dues from the sidebar to set the amount and billing schedule for a member’s unit.',
                side: 'right',
            },
        },
    ] : [
        {
            element: '[data-onboarding="nav-members"]',
            popover: {
                title: 'Members live here',
                description: 'This is where you will manage the people and units your association bills.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="new-member"]',
            popover: {
                title: 'Add your first member',
                description: 'Use this button to add details about a member of your association.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="import-members"]',
            popover: {
                title: 'Or import a member list',
                description: 'If you already have a roster, upload a CSV or Excel file and review it before anything is added.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="nav-dues"]',
            popover: {
                title: 'Then assign dues',
                description: 'After saving the member, choose Dues from the sidebar.',
                side: 'right',
            },
        },
    ],
    '/dues': () => [
        ...(!store.duesProgress.members ? [{
            element: '[data-onboarding="nav-members"]',
            popover: {
                title: 'Add a member first',
                description: 'Dues are assigned to a member’s unit, so add at least one member before continuing.',
                side: 'right',
            },
        }] : []),
        {
            element: '[data-onboarding="nav-dues"]',
            popover: {
                title: 'Dues live here',
                description: 'Use the Dues page to assign dues, change amounts, and manage each unit’s billing schedule.',
                side: 'right',
            },
        },
        {
            element: store.duesProgress.dues ? '[data-onboarding="dues-table"]' : '[data-onboarding="assign-dues"]',
            popover: store.duesProgress.dues ? {
                title: 'Dues are assigned',
                description: 'This schedule shows the amount and frequency of recurring dues.',
                side: 'top',
            } : {
                title: 'Assign dues',
                description: 'Choose a member, then enter the amount and how often their unit should be billed.',
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="nav-invoices"]',
            popover: {
                title: 'Next: preview the invoice',
                description: 'Choose Invoices to see an example invoice. Then connect to Stripe to start sending real invoices.',
                side: 'right',
            },
        },
    ],
    '/invoices': () => [
        ...(!store.duesProgress.dues ? [{
            element: '[data-onboarding="nav-dues"]',
            popover: {
                title: 'Assign dues first',
                description: 'The preview uses a real member and dues schedule, so assign dues before continuing.',
                side: 'right',
            },
        }] : []),
        {
            element: '[data-onboarding="nav-invoices"]',
            popover: {
                title: 'Invoices live here',
                description: 'Use the Invoices page to preview invoices, start recurring billing, send one-off invoices, and track payments.',
                side: 'right',
            },
        },
        {
            element: '[data-onboarding="preview-invoice"]',
            popover: {
                title: 'Preview without sending',
                description: 'See how an invoice will look using your member and dues details. A preview does not create an invoice or require Stripe.',
                side: 'bottom',
            },
        },
        ...(!store.duesProgress.payouts ? [{
            element: '[data-onboarding="nav-profile"]',
            popover: {
                title: 'Next: connect payouts',
                description: 'Open Profile to connect your association’s Stripe account. SmartHOA uses Stripe to securely process payments.',
                side: 'bottom',
            },
        }] : !store.duesProgress.billing ? [
            {
                element: '[data-onboarding="collect-dues"]',
                popover: {
                    title: 'Start recurring billing',
                    description: 'Use Collect Dues to start the assigned schedule for members who are ready to bill.',
                    side: 'bottom',
                },
            },
            {
                element: '[data-onboarding="new-invoice"]',
                popover: {
                    title: 'Or send a one-off invoice',
                    description: 'You can also send a single non-recurring invoice.',
                    side: 'bottom',
                },
            },
        ] : []),
    ],
    '/profile': () => [
        {
            element: '[data-onboarding="nav-profile"]',
            popover: {
                title: 'Payout settings live in Profile',
                description: "Return here to finish verification or manage your association's Stripe account.",
                side: 'bottom',
            },
        },
        {
            element: '[data-onboarding="payouts-section"]',
            popover: {
                title: store.duesProgress.payouts ? 'Payouts are connected' : 'Connect payouts',
                description: store.duesProgress.payouts
                    ? 'Your association can now send invoices and receive payments.'
                    : "Verify your association's Stripe account to collect payments.",
                side: 'top',
            },
        },
        ...(!store.duesProgress.payouts ? [{
            element: '[data-onboarding="setup-payouts"]',
            popover: {
                title: 'Set up your Stripe account',
                description: "Set up your association's Stripe to begin collecting payments.",
                side: 'top',
            },
        }] : [{
            element: '[data-onboarding="nav-invoices"]',
            popover: {
                title: 'Finish on Invoices',
                description: 'Start recurring billing or send a one-off invoice to complete dues setup.',
                side: 'right',
            },
        }]),
    ],
};

function stopTour() {
    if (startTimer) clearTimeout(startTimer);
    startTimer = null;
    if (tour.value?.isActive()) tour.value.destroy();
    tour.value = null;
}

async function startTour() {
    stopTour();
    const startingPath = route.path;
    const activePath = readOnboardingPath();
    const goal = activePath?.goal;
    const pageSteps = goal === 'accounting' ? accountingPageSteps : goal === 'dues' ? duesPageSteps : {};
    const makeSteps = pageSteps[startingPath];
    if (route.name === 'onboarding') return;
    if (!makeSteps || !isExpectedOnboardingPage(goal, startingPath)) return;

    try {
        if (goal === 'accounting') await store.refreshAccountingProgress();
        else await store.refreshDuesProgress();
    } catch (error) {
        console.error('Could not refresh onboarding progress:', error);
    }
    if (route.path !== startingPath || !isExpectedOnboardingPage(goal, startingPath)) return;

    const desktop = window.innerWidth > 991;
    if (desktop) layoutState.staticMenuDesktopInactive = false;
    await nextTick();
    startTimer = setTimeout(async () => {
        const isReport = goal === 'accounting' && startingPath === '/reports';
        let reportReady = false;
        if (isReport) {
            for (let attempt = 0; attempt < 30; attempt += 1) {
                reportReady = document.querySelector('[data-onboarding="reports-content"]')?.dataset.reportReady === 'true';
                if (reportReady) break;
                await new Promise((resolve) => setTimeout(resolve, 100));
                if (route.path !== startingPath) return;
            }
        }
        if (route.path !== startingPath) return;
        const { driver } = await import('driver.js');
        if (route.path !== startingPath || !isExpectedOnboardingPage(goal, startingPath)) return;
        const configuredSteps = makeSteps(reportReady);
        const steps = desktop ? configuredSteps : [
            {
                element: '[data-onboarding="nav-toggle"]',
                popover: {
                    title: 'Find these tools in the menu',
                    description: goal === 'accounting'
                        ? 'Budget, Bank Accounts, Transactions, and Reports are always available from this menu.'
                        : 'Members, Dues, and Invoices are always available from this menu.',
                    side: 'bottom',
                },
            },
            ...configuredSteps.filter((step) => !String(step.element).includes('nav-')),
        ];
        tour.value = driver({
            animate: true,
            allowKeyboardControl: true,
            overlayColor: '#17251f',
            overlayOpacity: 0.62,
            stagePadding: 8,
            stageRadius: 10,
            popoverClass: 'smarthoa-tour',
            showProgress: true,
            progressText: '{{current}} of {{total}}',
            nextBtnText: 'Next',
            prevBtnText: 'Back',
            doneBtnText: isReport && reportReady ? 'Report looks right' : 'Done',
            steps: steps.map((step) => ({ ...step, waitForElement: 2500, skipMissingElement: true })),
            onDoneClick: async (_element, _step, { driver: instance }) => {
                if (isReport && reportReady) {
                    try {
                        const completed = await store.completeAccountingSetup();
                        if (completed) toast.add({ severity: 'success', summary: 'Spending tracking is set up', life: 4000 });
                    } catch (error) {
                        toast.add({ severity: 'error', summary: 'Could not finish setup', detail: error.message || 'Please try again.', life: 6000 });
                    }
                }
                instance.destroy();
            },
        });
        tour.value.drive();
    }, 250);
}

watch(
    () => route.path,
    startTour,
    { immediate: true },
);

watch(
    () => [store.isAccountingComplete, store.isDuesComplete],
    ([accountingComplete, duesComplete]) => {
        const goal = readOnboardingPath()?.goal;
        if ((goal === 'accounting' && accountingComplete) || (goal === 'dues' && duesComplete)
            || !goal) stopTour();
    },
);

onBeforeUnmount(stopTour);
</script>

<template></template>

<style lang="scss">
.driver-popover.smarthoa-tour {
    --driver-popover-font-family: inherit;
    background: var(--p-surface-0);
    color: var(--p-text-color);
    border: 1px solid var(--p-surface-200);
    border-radius: 0.75rem;
    box-shadow: 0 18px 50px color-mix(in srgb, var(--p-surface-950) 22%, transparent);
    padding: 1rem;

    .driver-popover-title {
        color: var(--p-text-color);
        font-size: 1rem;
        font-weight: 600;
    }

    .driver-popover-description,
    .driver-popover-progress-text,
    .driver-popover-close-btn {
        color: var(--p-text-muted-color);
    }

    .driver-popover-footer-btn {
        border: 1px solid var(--p-primary-color);
        border-radius: 0.5rem;
        box-shadow: none;
        text-shadow: none;
    }

    .driver-popover-next-btn,
    .driver-popover-done-btn {
        background: var(--p-primary-color);
        color: var(--p-primary-contrast-color);
    }

    .driver-popover-prev-btn {
        background: transparent;
        border-color: var(--p-surface-300);
        color: var(--p-text-color);
    }
}

.driver-popover.smarthoa-tour .driver-popover-arrow-side-left.driver-popover-arrow {
    border-left-color: var(--p-surface-0);
}

.driver-popover.smarthoa-tour .driver-popover-arrow-side-right.driver-popover-arrow {
    border-right-color: var(--p-surface-0);
}

.driver-popover.smarthoa-tour .driver-popover-arrow-side-top.driver-popover-arrow {
    border-top-color: var(--p-surface-0);
}

.driver-popover.smarthoa-tour .driver-popover-arrow-side-bottom.driver-popover-arrow {
    border-bottom-color: var(--p-surface-0);
}
</style>
